/**
 * Location autocomplete – searchable dropdown for OoT locations.
 * Fetches from API via useAreas() or accepts a static list.
 * Supports tag-based filtering and location type filtering.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useAreas } from '@/queries/hooks';
import type { LocationDto } from '@/queries/types';
import { getAreaColor } from './data/area-colors';

interface LocationAutocompleteProps {
  /** Label/placeholder */
  label?: string;
  /** Filter by location type: 1 = overworld, 2 = dungeon */
  locationTypeId?: number;
  /** Current selection */
  value?: LocationDto | null;
  /** Called when selection changes */
  onChange?: (location: LocationDto | null) => void;
  /** Custom background class */
  className?: string;
  /** Width class */
  width?: string;
}

export function LocationAutocomplete({
  label = 'Location',
  locationTypeId,
  value,
  onChange,
  className = '',
  width = 'w-[180px]',
}: LocationAutocompleteProps) {
  const { data: areas } = useAreas();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Flatten areas into locations
  const allLocations = useMemo(() => {
    if (!areas) return [];
    let locs = areas.flatMap((area) =>
      area.locations.map((loc) => ({
        ...loc,
        areaName: area.name,
        areaColor: area.color,
      }))
    );
    if (locationTypeId) {
      locs = locs.filter((l) => l.locationTypeId === locationTypeId);
    }
    return locs.sort((a, b) => a.name.localeCompare(b.name));
  }, [areas, locationTypeId]);

  const filtered = useMemo(() => {
    if (!search) return allLocations;
    const s = search.toLowerCase();
    return allLocations.filter(
      (loc) =>
        loc.name.toLowerCase().includes(s) ||
        (loc.tags && loc.tags.toLowerCase().includes(s)) ||
        loc.areaName.toLowerCase().includes(s)
    );
  }, [allLocations, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleSelect = (loc: LocationDto) => {
    onChange?.(loc);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  // If a value is selected, show it as colored text that can be cleared
  if (value) {
    return (
      <div
        className={`${width} flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-700/50 transition-colors ${className}`}
        onClick={handleClear}
        onContextMenu={(e) => {
          e.preventDefault();
          onChange?.(null);
        }}
        title="Click to clear"
      >
        <span
          className="font-semibold text-sm truncate"
          style={{ color: getAreaColor(value.areaName) }}
        >
          {value.name}
        </span>
        <X size={12} className="text-gray-500 flex-shrink-0" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${width} ${className}`}>
      <div
        className="flex items-center gap-1 px-2 py-0.5 bg-gray-700/50 border border-gray-600/50 rounded cursor-pointer hover:border-gray-500 transition-colors"
        onClick={() => setOpen(!open)}
      >
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder-gray-500 min-w-0"
            placeholder={label}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 text-sm text-gray-400 truncate">{label}</span>
        )}
        <ChevronDown size={14} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-[240px] max-h-[250px] overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No locations found</div>
          ) : (
            filtered.map((loc) => (
              <button
                key={loc.id}
                onClick={() => handleSelect(loc)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getAreaColor(loc.areaName) }}
                />
                <span className="text-white truncate">{loc.name}</span>
                <span className="text-gray-500 text-xs ml-auto flex-shrink-0">{loc.areaName}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
